import React from 'react';
import { 
  Monitor, 
  Smartphone, 
  Tv, 
  Sparkles, 
  X, 
  Check, 
  Sliders, 
  HelpCircle,
  Keyboard,
  Gamepad2
} from 'lucide-react';
import { DeviceMode } from '../types';

interface DeviceModeSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMode: DeviceMode;
  onSelectMode: (mode: DeviceMode) => void;
  onOpenShortcuts: () => void;
}

export const DeviceModeSelectorModal: React.FC<DeviceModeSelectorModalProps> = ({
  isOpen,
  onClose,
  currentMode,
  onSelectMode,
  onOpenShortcuts
}) => {
  if (!isOpen) return null;

  const modes: {
    id: DeviceMode;
    title: string;
    description: string;
    icon: React.ReactNode;
    badge: string;
    features: string[];
  }[] = [
    {
      id: 'auto',
      title: 'Automàtic (Recomanat)',
      description: 'Detecta automàticament si ets a PC, mòbil, tauleta o televisor.',
      icon: <Sparkles className="w-6 h-6 text-amber-400" />,
      badge: 'Auto-Detect',
      features: ['Detecció del navegador i resolució', 'Adaptació instantània en redimensionar']
    },
    {
      id: 'pc',
      title: 'PC & Portàtil (Escriptori)',
      description: 'Optimitzat per a ratolí i teclat complet amb alta resolució.',
      icon: <Monitor className="w-6 h-6 text-cyan-400" />,
      badge: 'Mouse & Keyboard',
      features: ['Dreceres de teclat ràpides (Espai, F, M, /)', 'Punter i efectes hover rics', 'Graella de contingut ampliada']
    },
    {
      id: 'mobile',
      title: 'Mòbil & Tauleta (Tactil)',
      description: 'Disseny pensat per a pantalles tàctils petites i mitjanes.',
      icon: <Smartphone className="w-6 h-6 text-emerald-400" />,
      badge: 'Touch UI',
      features: ['Barra de navegació inferior d\'accés ràpid', 'Doble toc al reproductor (+10s / -10s)', 'Botons tàctils amples de 48px']
    },
    {
      id: 'tv',
      title: 'TV (Android TV & Fire TV)',
      description: 'Interfície 10-Foot optimitzada per a comandament a distància i D-Pad.',
      icon: <Tv className="w-6 h-6 text-amber-400" />,
      badge: '10-Foot D-Pad UI',
      features: ['Navegació espacial per fletxes ▲ ▼ ◄ ► i botó OK', 'Anell de selecció daurat d\'alta visibilitat', 'Lletra augmentada i marges de seguretat TV']
    }
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl bg-[#0e121d] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
              <Sliders className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-display font-bold text-xl text-white">
                Optimització de Dispositiu
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Adapta la interfície i els controls segons com vulguis gaudir d'AnimeCat.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {modes.map((m) => {
            const isSelected = currentMode === m.id;
            return (
              <div
                key={m.id}
                onClick={() => {
                  onSelectMode(m.id);
                }}
                className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 relative group ${
                  isSelected 
                    ? 'bg-amber-500/10 border-amber-400 shadow-lg shadow-amber-500/10' 
                    : 'bg-[#131724] border-white/5 hover:border-white/20 hover:bg-[#171d2e]'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      {m.icon}
                      <h4 className="font-display font-bold text-sm text-white">
                        {m.title}
                      </h4>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center shadow-md">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {m.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-white/5 space-y-1">
                  {m.features.map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <span className="text-amber-400">•</span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Helper Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-white/10">
          <button
            onClick={() => {
              onClose();
              onOpenShortcuts();
            }}
            className="flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
          >
            <Keyboard className="w-4 h-4" />
            <span>Veure Dreceres de Teclat & Comandament TV</span>
          </button>

          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shadow-md transition-all"
          >
            Fet
          </button>
        </div>

      </div>
    </div>
  );
};
